import {
  Calendar,
  CalendarCheck,
  CalendarX,
  CircleAlert,
  CircleCheck,
  CreditCard,
  FileText,
  Hash,
  MapPin,
  Tag,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmationDialog } from "@/components/confirmation-dialog/confirmation-dialog";
import { CopyableId } from "@/components/copyable-id";
import { InfoItem, SectionTitle } from "@/components/info-item";
import { Button } from "@/components/ui/button";
import { describePaymentAccount } from "@/features/payment-methods/helpers/payment-account";
import { formatCurrency } from "@/lib/currency-formatter";
import { formatISODate } from "@/lib/date-formatter";
import { formatKeyText } from "@/lib/utils";

import {
  Banner,
  solidPrimaryButtonClass,
} from "../../../../../components/banner";
import { getDecisionLabel } from "../helpers/get-decision-label";
import {
  isAcceptablePaymentKind,
  isCancelablePaymentKind,
  isFailedPayoutStatus,
  isFailedView,
  isPendingStatus,
} from "../types";

import { PanelActionBanner } from "./panel-action-banner";
import { PanelMileage } from "./panel-mileage";
import { PanelReceipt } from "./panel-receipt";
import { PaymentHeader } from "./payment-header";
import { RejectPaymentDialog } from "./reject-payment-dialog";

import type { CancelableState } from "../fetchers/use-cancelable-state";
import type { PaymentDetailsView } from "../types";

export interface PaymentDetailsLayoutProps {
  view: PaymentDetailsView;
  onChangePaymentMethod?: () => void;
  actions?: {
    isTaxCompliant: boolean;
    isTaxFormLoading: boolean;
    isActionPending: boolean;
    onAccept: () => void;
    onRejectConfirm: (reason: string) => void;
  };
  cancel?: CancelableState;
}

const dateValue = (iso: string) => formatISODate(iso, { format: "date" });

/** Section title for the type-specific details panel, keyed by claim kind. */
const SECTION_TITLE_KEY_BY_KIND: Record<
  PaymentDetailsView["details"]["kind"],
  string
> = {
  receipt: "claimDetail_section_receipt",
  mileage: "claimDetail_section_mileage",
  micropayment: "claimDetail_section_micropayment",
  stipend: "details",
};

/** Two-column group of {@link InfoItem}s, matching the trial details layout. */
const InfoGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">{children}</div>
);

export const PaymentDetailsLayout = ({
  view,
  onChangePaymentMethod,
  actions,
  cancel,
}: PaymentDetailsLayoutProps) => {
  const { t } = useTranslation();
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const isPending = isPendingStatus(view.status);
  const isAcceptable = isAcceptablePaymentKind(view.paymentKind);
  const showActions = Boolean(actions) && isPending && isAcceptable;
  const showChangePaymentMethod =
    isPending && isAcceptable && Boolean(view.paymentAccount);
  const isRejected = view.status.toLowerCase() === "rejected";
  const failed = isFailedView(view);
  const showCancel =
    Boolean(cancel) &&
    isPending &&
    isCancelablePaymentKind(view.paymentKind) &&
    !view.raisedByStudyTeam;

  const accountDescription = view.paymentAccount
    ? describePaymentAccount({
        accountType: view.paymentAccount.type,
        identifier: view.paymentAccount.identifier,
        title: view.paymentAccount.label,
        t,
      })
    : null;

  const visit = view.visit;
  const isReceipt = view.details.kind === "receipt";
  const showMicropaymentTitle =
    view.details.kind === "micropayment" &&
    Boolean(view.details.configurationLabel);
  const hasCustomDetails =
    view.details.kind === "mileage" ||
    isReceipt ||
    showMicropaymentTitle ||
    Boolean(view.decisionDate);
  const hasPaymentSection =
    Boolean(view.paymentAccount) || Boolean(view.payoutStatus);

  const detailsSectionTitleKey = SECTION_TITLE_KEY_BY_KIND[view.details.kind];

  const noteTitleKey = isRejected
    ? "claimDetail_rejectionReason"
    : view.adjustedAmount
      ? "claim_detail_adjustment_reason"
      : "claimDetail_reviewerNote";

  const DecisionIcon = isRejected ? CalendarX : CalendarCheck;
  const payoutFailed = isFailedPayoutStatus(view.payoutStatus);

  return (
    <div className="w-full space-y-5">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span className="font-medium">{t("payment_id")}:</span>
        <CopyableId id={view.id} ariaLabel="paymentId_copy" />
      </div>

      {failed && (
        <Banner
          tone="destructive"
          role="alert"
          data-test-id="payment-failed-banner"
          icon={<TriangleAlert aria-hidden="true" />}
          title={`${t("claimDetail_paymentFailedTitle")}. ${t(
            "claimDetail_paymentFailedMessage"
          )}`}
          description={view.payoutFailureReason}
          actions={
            onChangePaymentMethod && (
              <Button
                type="button"
                onClick={onChangePaymentMethod}
                className={solidPrimaryButtonClass}
                data-test-id="update-payment-method"
              >
                {t("claimDetail_updatePaymentMethod")}
              </Button>
            )
          }
        />
      )}

      {showActions && actions && (
        <PanelActionBanner
          isTaxCompliant={actions.isTaxCompliant}
          isTaxFormLoading={actions.isTaxFormLoading}
          isActionPending={actions.isActionPending}
          onAccept={() => setAcceptOpen(true)}
          onReject={() => setRejectOpen(true)}
        />
      )}

      <article className="w-full bg-white rounded-2xl border border-border shadow-xs">
        <div className="mx-auto px-5 sm:px-6 py-6">
          <PaymentHeader
            paymentKind={view.paymentKind}
            amount={view.amount}
            adjustedAmount={view.adjustedAmount}
            currency={view.currency}
            status={view.status}
            createdAt={view.createdAt}
            raisedByStudyTeam={view.raisedByStudyTeam}
          />

          <div className="mt-8 space-y-8">
            {view.externalNotes && (
              <section>
                <SectionTitle>{t(noteTitleKey)}</SectionTitle>
                <p className="text-muted-foreground wrap-break-word whitespace-pre-line leading-relaxed">
                  {view.externalNotes}
                </p>
              </section>
            )}

            {visit && (
              <section>
                <SectionTitle>{t("claimDetail_relatedVisit")}</SectionTitle>
                <InfoGrid>
                  <InfoItem
                    icon={<MapPin size={18} />}
                    label={t("claimDetail_visitName")}
                    value={visit.name}
                  />
                  <InfoItem
                    icon={<Calendar size={18} />}
                    label={t("claimDetail_visitDate")}
                    value={dateValue(visit.date)}
                  />
                  {visit.visitType && (
                    <InfoItem
                      icon={<Tag size={18} />}
                      label={t("claimDetail_visitType")}
                      value={visit.visitType}
                    />
                  )}
                  {visit.referenceId && (
                    <InfoItem
                      icon={<Hash size={18} />}
                      label={t("claimDetail_visitReference")}
                      value={visit.referenceId}
                    />
                  )}
                </InfoGrid>
              </section>
            )}

            {hasCustomDetails && (
              <section>
                <SectionTitle>{t(detailsSectionTitleKey)}</SectionTitle>
                {isReceipt && view.details.kind === "receipt" ? (
                  <PanelReceipt
                    details={view.details}
                    currency={view.currency}
                    total={view.amount}
                  />
                ) : (
                  <InfoGrid>
                    {view.details.kind === "mileage" && (
                      <PanelMileage details={view.details} />
                    )}
                    {showMicropaymentTitle &&
                      view.details.kind === "micropayment" && (
                        <InfoItem
                          icon={<FileText size={18} />}
                          label={t("name")}
                          value={view.details.configurationLabel}
                        />
                      )}
                  </InfoGrid>
                )}
              </section>
            )}

            {hasPaymentSection && (
              <section>
                <SectionTitle>{t("claimDetail_section_payment")}</SectionTitle>

                <InfoGrid>
                  {view.decisionDate && (
                    <InfoItem
                      icon={<DecisionIcon size={18} />}
                      label={getDecisionLabel(view.paymentKind, view.status, t)}
                      value={dateValue(view.decisionDate)}
                    />
                  )}
                  {view.payoutStatus && (
                    <InfoItem
                      icon={
                        payoutFailed ? (
                          <CircleAlert size={18} />
                        ) : (
                          <CircleCheck size={18} />
                        )
                      }
                      label={t("claimDetail_payoutStatus")}
                      value={
                        payoutFailed ? (
                          <span className="text-destructive">
                            {t("failed")}
                          </span>
                        ) : (
                          formatKeyText(t(view.payoutStatus))
                        )
                      }
                    />
                  )}
                </InfoGrid>
                {view.paymentAccount && (
                  <InfoItem
                    icon={<CreditCard size={18} />}
                    label={t("mileageReview_PaymentMethod")}
                    value={
                      <>
                        <p>{accountDescription ?? view.paymentAccount.label}</p>
                        {showChangePaymentMethod && (
                          <Button
                            onClick={onChangePaymentMethod}
                            variant="link"
                            size="sm"
                            className="capitalize underline p-0 h-auto text-sm font-normal mt-1"
                          >
                            {t("mileageReview_ChangePaymentMethod")}
                          </Button>
                        )}
                      </>
                    }
                  />
                )}
              </section>
            )}
          </div>

          {showCancel && cancel && (
            <div className="mt-8">
              <Button
                variant="link"
                size="sm"
                onClick={() => setCancelOpen(true)}
                loading={cancel.isCancelPending}
                data-test-id="cancel-claim-button"
                className="normal-case underline p-0 h-auto text-sm font-normal text-destructive hover:text-destructive"
              >
                {t("claimDetail_cancelThisClaim")}
              </Button>
            </div>
          )}
        </div>
      </article>

      {showActions && actions && (
        <>
          <ConfirmationDialog
            open={acceptOpen}
            label="accept-payment"
            onOpenChange={(open) => !open && setAcceptOpen(false)}
            title={t("payment_acceptConfirmationTitle")}
            message={t("payment_acceptConfirmationMessage", {
              amount: formatCurrency(view.amount, view.currency),
            })}
            confirmButtonText={t("accept")}
            cancelButtonText={t("cancel")}
            onConfirm={() => {
              setAcceptOpen(false);
              actions.onAccept();
            }}
            onDismiss={() => setAcceptOpen(false)}
          />
          <RejectPaymentDialog
            open={rejectOpen}
            paymentKind={view.paymentKind}
            isSubmitting={actions.isActionPending}
            onOpenChange={setRejectOpen}
            onConfirm={(reason) => {
              actions.onRejectConfirm(reason);
              setRejectOpen(false);
            }}
          />
        </>
      )}

      {showCancel && cancel && (
        <ConfirmationDialog
          open={cancelOpen}
          label="cancel-claim"
          onOpenChange={(open) => !open && setCancelOpen(false)}
          title={t("claimDetail_cancelClaimTitle")}
          message={t("claimDetail_cancelClaimMessage")}
          confirmButtonText={t("claimDetail_cancelClaim")}
          cancelButtonText={t("claimDetail_keepClaim")}
          onConfirm={() => {
            setCancelOpen(false);
            cancel.onCancelConfirm();
          }}
          onDismiss={() => setCancelOpen(false)}
        />
      )}
    </div>
  );
};
